class JournalCarrousel extends HTMLElement {
  connectedCallback() {
    this.spoor = this.querySelector('[data-jc-spoor]');
    this.pijlen = this.querySelector('[data-jc-pijlen]');
    this.bolletjes = this.querySelector('[data-jc-bolletjes]');
    if (!this.spoor) return;

    this.vorige = this.querySelector('[data-jc-richting="vorige"]');
    this.volgende = this.querySelector('[data-jc-richting="volgende"]');

    this.controleer = this.controleer.bind(this);

    /* Alle listeners aan één signaal, zodat disconnectedCallback ze samen
       weghaalt. Wordt het element verplaatst (in de editor bij het
       herschikken van secties), dan draait connectedCallback opnieuw; de
       pijlen kregen dan een tweede click-listener en schoven twee pagina's
       per klik. */
    this.afbreken = new AbortController();
    const { signal } = this.afbreken;

    this.vorige?.addEventListener('click', () => this.schuif(-1), { signal });
    this.volgende?.addEventListener('click', () => this.schuif(1), { signal });

    this.spoor.addEventListener('scroll', this.controleer, { passive: true, signal });

    /* Bij een andere breedte passen er andere aantallen kaarten, en dan
       verandert ook het aantal bolletjes. Een ResizeObserver op het spoor
       ziet dat, ook als het venster zelf niet verandert. */
    this.waarnemer = new ResizeObserver(this.controleer);
    this.waarnemer.observe(this.spoor);

    this.controleer();
  }

  disconnectedCallback() {
    this.waarnemer?.disconnect();
    this.afbreken?.abort();
  }

  /* --- Maten -------------------------------------------------------------- */

  /* Eén stap is de breedte van een kaart plus de tussenruimte. Die ruimte
     uit de CSS lezen in plaats van hem hier te herhalen: staat de gap straks
     op een andere waarde, dan klopt dit nog. */
  stap() {
    const kaart = this.spoor.querySelector('.jc__item');
    if (!kaart) return this.spoor.clientWidth;
    const gap = parseFloat(getComputedStyle(this.spoor).columnGap) || 0;
    return kaart.getBoundingClientRect().width + gap;
  }

  /* Hoeveel kaarten er in één beeld passen. Naar beneden afronden met een
     kleine marge, want subpixels maken van 2 kaarten anders 1,98. */
  perPagina() {
    const stap = this.stap();
    if (!stap) return 1;
    return Math.max(1, Math.round((this.spoor.clientWidth + 1) / stap));
  }

  paginas() {
    const totaal = this.spoor.querySelectorAll('.jc__item').length;
    return Math.max(1, Math.ceil(totaal / this.perPagina()));
  }

  huidigePagina() {
    const stap = this.stap();
    if (!stap) return 0;

    // Helemaal rechts is altijd de laatste pagina. Bij een oneven aantal
    // kaarten kan het spoor niet tot het begin van die pagina schuiven, en
    // bleef het laatste bolletje zo nooit actief.
    const { scrollLeft, scrollWidth, clientWidth } = this.spoor;
    if (scrollLeft > 2 && scrollLeft + clientWidth >= scrollWidth - 2) return this.paginas() - 1;

    const index = Math.round(this.spoor.scrollLeft / stap);
    return Math.min(this.paginas() - 1, Math.floor(index / this.perPagina()));
  }

  /* --- Schuiven ----------------------------------------------------------- */

  zacht() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'auto'
      : 'smooth';
  }

  schuif(richting) {
    this.spoor.scrollBy({
      left: richting * this.stap() * this.perPagina(),
      behavior: this.zacht(),
    });
  }

  naarPagina(nummer) {
    this.spoor.scrollTo({
      left: nummer * this.stap() * this.perPagina(),
      behavior: this.zacht(),
    });
  }

  /* --- Bijhouden ---------------------------------------------------------- */

  controleer() {
    const { scrollLeft, scrollWidth, clientWidth } = this.spoor;
    // Marge van 2px: browsers ronden scrollLeft af, en dan blijft een knop
    // ten onrechte actief aan het einde van de rij.
    const aanBegin = scrollLeft <= 2;
    const aanEinde = scrollLeft + clientWidth >= scrollWidth - 2;
    const past = scrollWidth <= clientWidth + 2;

    if (this.pijlen) this.pijlen.hidden = past;
    if (this.vorige) this.vorige.disabled = aanBegin;
    if (this.volgende) this.volgende.disabled = aanEinde;

    this.werkBolletjesBij(past);
  }

  werkBolletjesBij(past) {
    if (!this.bolletjes) return;

    const aantal = this.paginas();
    this.bolletjes.hidden = past || aantal < 2;
    if (this.bolletjes.hidden) return;

    // Alleen opnieuw opbouwen als het aantal echt verandert
    if (this.bolletjes.children.length !== aantal) {
      // Het label komt vertaald uit de sectie (data-jc-bol-label), met
      // [huidige] en [totaal] als plaatshouders. Het Nederlands hieronder is
      // alleen een terugval voor als dat attribuut ontbreekt.
      const sjabloon = this.bolletjes.dataset.jcBolLabel || 'Pagina [huidige] van [totaal]';
      this.bolletjes.textContent = '';
      for (let i = 0; i < aantal; i++) {
        const bol = document.createElement('button');
        bol.type = 'button';
        bol.className = 'jc-bol';
        bol.setAttribute('role', 'tab');
        bol.setAttribute(
          'aria-label',
          sjabloon.replace('[huidige]', String(i + 1)).replace('[totaal]', String(aantal))
        );
        bol.addEventListener('click', () => this.naarPagina(i));
        this.bolletjes.appendChild(bol);
      }
    }

    const nu = this.huidigePagina();
    Array.from(this.bolletjes.children).forEach((bol, i) => {
      bol.setAttribute('aria-selected', String(i === nu));
    });
  }
}

if (!customElements.get('journal-carrousel')) {
  customElements.define('journal-carrousel', JournalCarrousel);
}